import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, useCallback } from 'react';
import { z } from 'zod';
import { Plug, CheckCircle2, ExternalLink, FileUp, Cpu, AlertTriangle, Gauge } from 'lucide-react';
import { cn } from '@siliconx/shared/ui/utils';
import { LoadingShell } from '@siliconx/shared/ui/state';
import type { DatadogCoverageReport } from '@siliconx/shared';
import { useAuth } from '../lib/authContext';
import { api, throwIfNotOk } from '../lib/api';
import { ErpRegisterCard } from '../components/erp/ErpRegisterCard';
import { BmcCredentialsCard } from '../components/verification/BmcCredentialsCard';
import { Button } from '@siliconx/shared/ui/Button';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { requireOrgFeature } from '../lib/orgFeatureGate';

// RUF-378: v1 is inbound-connection integrations only (Datadog, Prometheus,
// bulk CSV import). No outbound "Partners" section — locked decision.

// 721: the `connected` / `integrationId` / `error` search params existed
// solely for the Datadog OAuth callback to hand a result back to this page
// through a browser redirect. Connect is now an in-page POST, so the result
// is in the response and the params are gone.
// SIL-1108: `orgFeature: 'fleetData'` (customerNav.ts) is business-only —
// gated here so a direct hit doesn't fire this page's mount-time fetch
// before the root `OrgFeatureGate`'s (__root.tsx) redirect lands.
export const Route = createFileRoute('/integrations')({
  beforeLoad: () => requireOrgFeature('fleetData'),
  component: IntegrationsPage,
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DatadogStatus {
  connected: boolean;
  integrationId?: string;
  /** Null when the app key could not read org metadata at connect time (721). */
  ddOrgName?: string | null;
  ddSite?: string;
  lastSyncedAt?: string | null;
  connectedAt?: string | null;
  connectedByEmail?: string | null;
  coverage?: DatadogCoverageReport;
}

/**
 * Datadog sites, mirroring DATADOG_SITES in apps/api/src/lib/datadog-keys.ts.
 * Keys are site-scoped, and picking the wrong one fails with the same error
 * as a bad key, so the picker is deliberately explicit rather than defaulted
 * and hidden.
 */
const DATADOG_SITE_OPTIONS = [
  { value: 'datadoghq.com', label: 'US1 (datadoghq.com)' },
  { value: 'us3.datadoghq.com', label: 'US3 (us3.datadoghq.com)' },
  { value: 'us5.datadoghq.com', label: 'US5 (us5.datadoghq.com)' },
  { value: 'datadoghq.eu', label: 'EU1 (datadoghq.eu)' },
  { value: 'ddog-gov.com', label: 'US1-FED (ddog-gov.com)' },
  { value: 'ap1.datadoghq.com', label: 'AP1 (ap1.datadoghq.com)' },
] as const;

/** Kept in sync with the API's zod enum — a widened `string` here would only
 * fail at runtime with an opaque 400. */
type DatadogSiteOption = (typeof DATADOG_SITE_OPTIONS)[number]['value'];

interface PrometheusTarget {
  id: string;
  label: string;
  scrapeUrl: string;
  isActive: boolean;
}

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------

function Banner({
  message,
  type,
  onDismiss,
}: {
  message: string;
  type: 'success' | 'error';
  onDismiss: () => void;
}) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4 rounded-lg border px-4 py-3 text-sm',
        type === 'success'
          ? 'border-green-200 bg-green-50 text-green-800'
          : 'border-red-200 bg-red-50 text-red-800',
      )}
    >
      <span>{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        className="text-xs font-medium underline opacity-70 hover:opacity-100"
      >
        Dismiss
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card shell
// ---------------------------------------------------------------------------

function IntegrationCard({
  icon: Icon,
  logo,
  title,
  description,
  children,
}: {
  icon?: typeof Plug;
  // Full-color brand SVG in /service-logos; takes precedence over `icon`.
  logo?: string;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="space-y-3">
      <div className="flex items-center gap-2">
        {logo ? (
          <img src={logo} alt="" className="size-5 shrink-0" />
        ) : Icon ? (
          <Icon className="size-5 shrink-0 text-muted-foreground" />
        ) : null}
        <PanelTitle title={title} description={description} className="min-w-0 flex-1" />
      </div>
      {children}
    </Card>
  );
}

function ConnectedBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
      <CheckCircle2 className="size-3" /> Connected
    </span>
  );
}

function NotConnectedBadge() {
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
      Not connected
    </span>
  );
}

// ---------------------------------------------------------------------------
// Datadog coverage report (SIL-151) — connect-time AND settings-page:
// namespaces found, v5 field coverage, confidence expectation, ECC-counters
// recommendation. Renders from the connection's discovery record; a null
// namespacesDiscoveredAt is a normal "pending" state, not an error.
// ---------------------------------------------------------------------------

const COVERAGE_STATE_BADGE: Record<DatadogCoverageReport['state'], { label: string; className: string }> = {
  pending: { label: 'Discovery pending', className: 'bg-muted text-muted-foreground' },
  insufficient: { label: 'Insufficient telemetry source', className: 'bg-amber-100 text-amber-800' },
  covered: { label: 'Coverage detected', className: 'bg-green-100 text-green-700' },
};

function CoverageReportSection({ coverage }: { coverage: DatadogCoverageReport }) {
  const badge = COVERAGE_STATE_BADGE[coverage.state];

  return (
    <div className="space-y-2 rounded-md border border-border bg-muted p-3">
      <div className="flex items-center gap-2">
        <Gauge className="size-3.5 text-muted-foreground" />
        <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', badge.className)}>
          {badge.label}
        </span>
      </div>

      <p className="text-xs text-muted-foreground">{coverage.confidenceExpectation}</p>

      {coverage.state === 'covered' && (
        <>
          <div className="text-xs text-muted-foreground">
            Namespaces detected:{' '}
            <span className="font-medium text-foreground">{coverage.detectedPrefixes.join(', ')}</span>
          </div>
          <div className="text-xs text-muted-foreground">
            <div className="font-medium text-muted-foreground">Fields covered ({coverage.coveredFields.length}):</div>
            <div className="mt-1 flex flex-wrap gap-1">
              {coverage.coveredFields.map((f) => (
                <span
                  key={f.telemetryName}
                  className="rounded bg-card px-1.5 py-0.5 text-xs text-muted-foreground ring-1 ring-inset ring-border"
                >
                  {f.label}
                </span>
              ))}
            </div>
          </div>
        </>
      )}

      {/* SIL-469: the recommendation text is owned by the shared coverage
          module, not hardcoded here. The previous copy told sellers to add
          ECC counters to dcgm-exporter — advice that cannot work, because the
          Datadog DCGM check drops ECC regardless of exporter config. */}
      {coverage.recommendation && (
        <div className="space-y-1.5 rounded-md border border-amber-200 bg-amber-50 p-2">
          <div className="flex items-center gap-1.5 text-xs font-medium text-amber-800">
            <AlertTriangle className="size-3" />
            {coverage.recommendation.title}
          </div>
          <p className="text-xs text-amber-700">{coverage.recommendation.body}</p>
          {coverage.recommendation.snippet && (
            <pre className="overflow-x-auto rounded bg-foreground p-2 text-xs leading-snug text-zinc-100">
              {coverage.recommendation.snippet}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Datadog card
// ---------------------------------------------------------------------------

function DatadogCard({
  isAdmin,
  onConnected,
}: {
  isAdmin: boolean;
  onConnected: (message: string) => void;
}) {
  const [status, setStatus] = useState<DatadogStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // 721 key-entry form state. Kept in component state only — never persisted
  // to localStorage or a URL, and cleared as soon as the POST succeeds.
  const [showForm, setShowForm] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [appKey, setAppKey] = useState('');
  const [site, setSite] = useState<DatadogSiteOption>(DATADOG_SITE_OPTIONS[0].value);

  const load = useCallback(() => {
    setLoading(true);
    api.api.client.integrations.datadog.status
      .$get()
      .then(async (res) => {
        await throwIfNotOk(res);
        return (await res.json()) as unknown as DatadogStatus;
      })
      .then(setStatus)
      .catch((ex: unknown) => setErr((ex as Error).message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  /**
   * 721: submits the key pair to the API, which validates it against Datadog
   * before storing. A rejected pair leaves the form filled in so the user can
   * correct it rather than retyping both keys.
   */
  async function handleConnect(e: React.FormEvent) {
    e.preventDefault();
    setConnecting(true);
    setErr(null);
    try {
      const res = await api.api.client.integrations.datadog.connect.$post({
        json: { apiKey: apiKey.trim(), appKey: appKey.trim(), site },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      // Drop the plaintext keys from memory as soon as they are accepted.
      setApiKey('');
      setAppKey('');
      setShowForm(false);
      onConnected('Datadog connected successfully.');
      load();
    } catch (ex: unknown) {
      setErr((ex as Error).message);
    } finally {
      setConnecting(false);
    }
  }

  async function handleDisconnect() {
    if (!status?.integrationId) return;
    if (!confirm('Disconnect Datadog? SiliconX will stop syncing telemetry from this org.')) return;
    setRevoking(true);
    try {
      const res = await api.api.client.integrations.datadog.$delete();
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      load();
    } catch (ex: unknown) {
      setErr((ex as Error).message);
    } finally {
      setRevoking(false);
    }
  }

  return (
    <IntegrationCard
      logo="/service-logos/datadog.svg"
      title="Datadog"
      description="Connect a Datadog org to sync telemetry for your fleet automatically."
    >
      {loading ? (
        <LoadingShell rows={1} />
      ) : err ? (
        <div className="text-xs text-red-600">{err}</div>
      ) : status?.connected ? (
        <div className="space-y-2">
          <ConnectedBadge />
          <div className="text-xs text-muted-foreground">
            <div>
              Org:{' '}
              <span className="font-medium text-foreground">
                {/* ddOrgName is null when the app key can't read org metadata
                    (721) — the site is always known, so show that instead of
                    an empty label. */}
                {status.ddOrgName ?? status.ddSite ?? 'connected'}
              </span>
            </div>
            <div>
              Last synced:{' '}
              {status.lastSyncedAt ? new Date(status.lastSyncedAt).toLocaleString() : 'never'}
            </div>
            <div>
              Connected:{' '}
              {status.connectedAt ? new Date(status.connectedAt).toLocaleString() : 'unknown'}
              {status.connectedByEmail ? ` by ${status.connectedByEmail}` : ''}
            </div>
          </div>
          {status.coverage && <CoverageReportSection coverage={status.coverage} />}
          {isAdmin ? (
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={revoking}
              className="rounded-md px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              {revoking ? 'Disconnecting…' : 'Disconnect'}
            </button>
          ) : (
            <p className="text-xs text-muted-foreground">Only an org admin can disconnect.</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <NotConnectedBadge />
          {!isAdmin ? (
            <p className="text-xs text-muted-foreground">Only an org admin can connect Datadog.</p>
          ) : !showForm ? (
            <Button size="sm" onClick={() => setShowForm(true)}>
              Connect
            </Button>
          ) : (
            <form onSubmit={handleConnect} className="space-y-2">
              <label className="block text-xs font-medium text-muted-foreground">
                Datadog site
                <select
                  value={site}
                  onChange={(ev) => setSite(ev.target.value as DatadogSiteOption)}
                  className="mt-1 w-full rounded-md border border-border px-2 py-1 text-xs"
                >
                  {DATADOG_SITE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-xs font-medium text-muted-foreground">
                API key
                <input
                  type="password"
                  autoComplete="off"
                  value={apiKey}
                  onChange={(ev) => setApiKey(ev.target.value)}
                  placeholder="Organization Settings → API Keys"
                  className="mt-1 w-full rounded-md border border-border px-2 py-1 font-mono text-xs"
                />
              </label>
              <label className="block text-xs font-medium text-muted-foreground">
                Application key
                <input
                  type="password"
                  autoComplete="off"
                  value={appKey}
                  onChange={(ev) => setAppKey(ev.target.value)}
                  placeholder="Organization Settings → Application Keys"
                  className="mt-1 w-full rounded-md border border-border px-2 py-1 font-mono text-xs"
                />
              </label>
              <p className="text-xs text-muted-foreground">
                The application key needs <span className="font-mono">timeseries_query</span> and{' '}
                <span className="font-mono">metrics_read</span> scopes.{' '}
                <a
                  href="https://docs.datadoghq.com/account_management/api-app-keys/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-0.5 underline"
                >
                  Where to find these <ExternalLink className="size-3" />
                </a>
              </p>
              <div className="flex gap-2">
                <Button size="sm" type="submit" disabled={connecting || !apiKey.trim() || !appKey.trim()}>
                  {connecting ? 'Verifying…' : 'Connect'}
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setApiKey('');
                    setAppKey('');
                    setErr(null);
                  }}
                  className="rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </IntegrationCard>
  );
}

// ---------------------------------------------------------------------------
// Prometheus card — status/config only, no new backend (RUF-378 ponytail scope)
// ---------------------------------------------------------------------------

function PrometheusCard() {
  const [targets, setTargets] = useState<PrometheusTarget[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.api.client['prometheus-targets']
      .$get()
      .then(async (res) => {
        await throwIfNotOk(res);
        return (await res.json()) as unknown as PrometheusTarget[];
      })
      .then(setTargets)
      .catch((ex: unknown) => setErr((ex as Error).message));
  }, []);

  const activeCount = targets?.filter((t) => t.isActive).length ?? 0;

  return (
    <IntegrationCard
      logo="/service-logos/prometheus.svg"
      title="Prometheus"
      description="Register scrape targets so SiliconX can pull telemetry from your Prometheus instances."
    >
      {err ? (
        <div className="text-xs text-red-600">{err}</div>
      ) : targets === null ? (
        <LoadingShell rows={1} />
      ) : targets.length === 0 ? (
        <div className="space-y-2">
          <NotConnectedBadge />
          <p className="text-xs text-muted-foreground">
            No scrape targets registered yet. Manage targets from{' '}
            <a href="/locations" className="underline hover:text-muted-foreground">
              Locations
            </a>{' '}
            or your telemetry configuration.
          </p>
        </div>
      ) : (
        <div className="space-y-1">
          <ConnectedBadge />
          <div className="text-xs text-muted-foreground">
            {targets.length} target{targets.length !== 1 ? 's' : ''} registered, {activeCount} active
          </div>
        </div>
      )}
    </IntegrationCard>
  );
}

// ---------------------------------------------------------------------------
// Discovered assets (SIL-149) — provisional GPUs auto-created by the
// Datadog adapter (SIL-148's syncDiscoveredAssets upsert). Never shown as
// portfolio assets until confirmed — this card IS the confirm/dismiss UI.
// ---------------------------------------------------------------------------

interface SkuSuggestion {
  skuId: string;
  modelName: string | null;
  verifiedSku: string;
}

interface DiscoveredAsset {
  id: string;
  gpuUuid: string;
  hostnameRaw: string | null;
  deviceNameRaw: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  skuSuggestions: SkuSuggestion[];
}

const LIFECYCLE_OPTIONS = [
  { id: 'active', label: 'Active' },
  { id: 'idle', label: 'Idle' },
  { id: 'in_transit', label: 'In Transit' },
  { id: 'quarantined', label: 'Quarantined' },
];

function DiscoveredAssetRow({
  row,
  onResolved,
}: {
  row: DiscoveredAsset;
  onResolved: (id: string) => void;
}) {
  const [lifecycleStateId, setLifecycleStateId] = useState('active');
  const [skuId, setSkuId] = useState<string>('');
  const [existingAssetId, setExistingAssetId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function confirm() {
    setBusy(true);
    setErr(null);
    try {
      const res = await api.api.client['datadog-discoveries'][':id'].confirm.$post({
        param: { id: row.id },
        json: existingAssetId.trim()
          ? { existingAssetId: existingAssetId.trim() }
          : {
              assetClassId: 'gpu',
              lifecycleStateId,
              skuId: skuId || undefined,
            },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      onResolved(row.id);
    } catch (ex: unknown) {
      setErr((ex as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function dismiss() {
    if (!window.confirm('Dismiss this discovered GPU? It will not be re-shown.')) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.api.client['datadog-discoveries'][':id'].dismiss.$post({
        param: { id: row.id },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      onResolved(row.id);
    } catch (ex: unknown) {
      setErr((ex as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-xs font-medium text-foreground">
            {row.deviceNameRaw ?? 'Unknown GPU model'}
          </div>
          <div className="text-xs text-muted-foreground">
            {row.hostnameRaw ? `Host: ${row.hostnameRaw} · ` : ''}
            GPU UUID: {row.gpuUuid}
          </div>
        </div>
      </div>

      {row.skuSuggestions.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>SKU match:</span>
          <select
            className="rounded border border-border px-1.5 py-0.5 text-xs"
            value={skuId}
            onChange={(e) => setSkuId(e.target.value)}
          >
            <option value="">Unmatched (confirm as-is)</option>
            {row.skuSuggestions.map((s) => (
              <option key={s.skuId} value={s.skuId}>
                {s.modelName ?? s.verifiedSku}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <select
          className="rounded border border-border px-1.5 py-1 text-xs"
          value={lifecycleStateId}
          onChange={(e) => setLifecycleStateId(e.target.value)}
          disabled={!!existingAssetId.trim()}
        >
          {LIFECYCLE_OPTIONS.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Already-tracked asset ID (optional — links instead of creating)"
          value={existingAssetId}
          onChange={(e) => setExistingAssetId(e.target.value)}
          className="min-w-[220px] flex-1 rounded border border-border px-1.5 py-1 text-xs"
        />
        <Button size="sm"
          onClick={confirm}
          disabled={busy}
        >
          {existingAssetId.trim() ? 'Link' : 'Confirm'}
        </Button>
        <button
          type="button"
          onClick={dismiss}
          disabled={busy}
          className="rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
        >
          Dismiss
        </button>
      </div>
      {err && <div className="text-xs text-red-600">{err}</div>}
    </div>
  );
}

function DiscoveredAssetsCard({ isAdmin }: { isAdmin: boolean }) {
  const [rows, setRows] = useState<DiscoveredAsset[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    api.api.client['datadog-discoveries']
      .$get()
      .then(async (res) => {
        await throwIfNotOk(res);
        return (await res.json()) as unknown as { data: DiscoveredAsset[] };
      })
      .then((r) => setRows(r.data))
      .catch((ex: unknown) => setErr((ex as Error).message));
  }, []);

  useEffect(load, [load]);

  function handleResolved(id: string) {
    setRows((prev) => (prev ? prev.filter((r) => r.id !== id) : prev));
  }

  return (
    <Card className="space-y-3 md:col-span-2 lg:col-span-3">
      <div className="flex items-center gap-2">
        <Cpu className="size-5 shrink-0 text-muted-foreground" />
        <PanelTitle
          title="Discovered assets"
          description="GPUs Datadog has reported telemetry for but that aren't yet in your portfolio. Confirm to add them as tracked assets, or dismiss ones you don't want SiliconX to manage."
          className="min-w-0 flex-1"
        />
      </div>
      {err ? (
        <div className="text-xs text-red-600">{err}</div>
      ) : rows === null ? (
        <LoadingShell rows={1} />
      ) : rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">No unconfirmed discoveries right now.</p>
      ) : !isAdmin ? (
        <p className="text-xs text-muted-foreground">
          {rows.length} discovered GPU{rows.length !== 1 ? 's' : ''} awaiting confirmation. Only
          an org admin can confirm or dismiss.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <DiscoveredAssetRow key={row.id} row={row} onResolved={handleResolved} />
          ))}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Bulk import card
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// ServiceNow card — frontend mock (SIL-211). Client-side only: the connected
// state is faked in-memory and resets on reload. Backend wiring is SN-1..SN-4
// (SIL-207..210); when it lands, swap this useState for the Datadog card's
// status/authorize-url/DELETE calls.
// ---------------------------------------------------------------------------

function ServiceNowCard({ isAdmin }: { isAdmin: boolean }) {
  // ponytail: mock connection state, resets on reload — no persistence until backend exists.
  const [connected, setConnected] = useState(false);

  return (
    <IntegrationCard
      logo="/service-logos/servicenow.svg"
      title="ServiceNow"
      description="Sync assets from your ServiceNow CMDB automatically."
    >
      {connected ? (
        <div className="space-y-2">
          <ConnectedBadge />
          <div className="text-xs text-muted-foreground">
            <div>Instance: <span className="font-medium text-foreground">acme.service-now.com</span></div>
            <div>Last synced: just now</div>
            <div>312 assets synced</div>
          </div>
          {isAdmin ? (
            <button
              type="button"
              onClick={() => {
                if (confirm('Disconnect ServiceNow? SiliconX will stop syncing assets from this instance.')) {
                  setConnected(false);
                }
              }}
              className="rounded-md px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
            >
              Disconnect
            </button>
          ) : (
            <p className="text-xs text-muted-foreground">Only an org admin can disconnect.</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <NotConnectedBadge />
          {isAdmin ? (
            <Button size="sm" className="gap-1 inline-flex items-center"
              onClick={() => setConnected(true)}
            >
              Connect <ExternalLink className="size-3" />
            </Button>
          ) : (
            <p className="text-xs text-muted-foreground">Only an org admin can connect ServiceNow.</p>
          )}
        </div>
      )}
    </IntegrationCard>
  );
}

function BulkImportCard() {
  return (
    <IntegrationCard
      icon={FileUp}
      title="Bulk Import"
      description="Import assets in bulk from a CSV file."
    >
      <p className="text-xs text-muted-foreground">
        Download the CSV template, fill in one row per asset, and upload it. You can
        also start an import from the Assets page.
      </p>
      <a
        href="/onboarding"
        className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
      >
        Go to import <ExternalLink className="size-3" />
      </a>
    </IntegrationCard>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function IntegrationsPage() {
  const { user } = useAuth();
  const [banner, setBanner] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const isAdmin = user?.role === 'admin';

  return (
    <div className="space-y-6">
      <div>
        <PageHeader title="Integrations" pinTo="/integrations" />
        <p className="mt-1 text-sm text-muted-foreground">
          Connect data sources so SiliconX can keep your fleet&apos;s telemetry up to date.
        </p>
      </div>

      {banner && <Banner {...banner} onDismiss={() => setBanner(null)} />}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <DatadogCard isAdmin={isAdmin} onConnected={(m) => setBanner({ message: m, type: 'success' })} />
        <PrometheusCard />
        <ServiceNowCard isAdmin={isAdmin} />
        <BulkImportCard />
        <ErpRegisterCard isAdmin={isAdmin} />
        <DiscoveredAssetsCard isAdmin={isAdmin} />
        <BmcCredentialsCard isAdmin={isAdmin} />
      </div>
    </div>
  );
}
